/**
 * @licence Copyright © 2019 Mercury Redstone BV, all rights reserved
 */
import { useMemo, type HTMLAttributes } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Link as DefLink, Paper } from '@mui/material';
import { colors } from '../utils/colors';
import { SiteData } from '../utils/consts';
import {
  ipadCssScrollFix,
  getColor,
  getDownMedia,
  getUpMedia,
} from '../styles';
import logo from '../assets/images/round-white-logo.svg';
import { ScrollToTop } from '../components';
import { AppFooter } from '../components/AppFooter';
import { Text } from '../components/texts';
import { paths } from '../pages';

export type AuthLayoutProps = {
  withTopRightLogo?: boolean;
} & HTMLAttributes<HTMLDivElement>;

const AuthLayout = ({
  children,
  withTopRightLogo = false,
  ...props
}: AuthLayoutProps) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const withGoogleCaptchaTerms = useMemo(
    () => [paths.login, paths.signup].some((p) => p === pathname),
    [pathname]
  );

  const homepageTextContent = useMemo(
    () => (
      <>
        {t('AUTH_LAYOUT__homepageLink1')}{' '}
        <Link href={SiteData.ROOT_LINK}>{t('AUTH_LAYOUT__homepageLink2')}</Link>
      </>
    ),
    [t]
  );

  return (
    <Wrapper>
      <ScrollToTop />
      <ContentWrapper>
        <TabletHomepageText>{homepageTextContent}</TabletHomepageText>
        <ContentContainer>
          <Content elevation={1} {...props}>
            <MobileHomepageText>{homepageTextContent}</MobileHomepageText>
            {withTopRightLogo && <TopRightLogo src={logo} />}
            {children}
          </Content>
        </ContentContainer>
      </ContentWrapper>
      <AppFooter withGoogleCaptchaTerms={withGoogleCaptchaTerms} />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  background-color: ${colors.lightBlue};
  ${ipadCssScrollFix};
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 10px;
  position: relative;
  flex-grow: 1;

  ${getDownMedia('sm')} {
    margin-top: 0;
  }
`;

const Link = styled(DefLink)``;

const HomepageText = styled(Text)`
  font-size: 15px;
  color: ${getColor('shuttleGray')};
  ${Link} {
    font-weight: 400;
  }
`;

const TabletHomepageText = styled(HomepageText)`
  margin-left: 26px;
  position: relative;
  top: 4px;

  ${getDownMedia('sm')} {
    display: none;
  }
`;

const MobileHomepageText = styled(HomepageText)`
  align-self: flex-start;
  margin-bottom: 8px;
  position: relative;
  top: -16px;
  left: -24px;
  ${getUpMedia('sm')} {
    display: none;
  }
`;

const ContentContainer = styled.main`
  width: 100%;
  max-width: 600px;
  flex-grow: 1;
  align-self: center;
  display: flex;
  justify-content: center;
  align-items: center;

  ${getDownMedia('sm')} {
    max-width: 100%;
    align-items: stretch;
  }
`;

const Content = styled(Paper)`
  max-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 44px 37px;
  margin: 20px;
  position: relative;

  ${getDownMedia('sm')} {
    height: 100%;
    padding-top: 30px;
    padding-left: 37px;
    padding-right: 37px;
    margin: 0;
    box-shadow: none;
    border-radius: 0;
  }

  ${getDownMedia(376)} {
    padding-left: 30px;
    padding-right: 30px;
  }
`;

const TopRightLogo = styled.img`
  width: 54px;
  height: 54px;
  position: absolute;
  top: 20px;
  right: 20px;
`;

export { AuthLayout };
